import { VueConstructor } from "vue";
let observer: IntersectionObserver | null = null;
interface ElementClasses {
  initial: string;
  loaded: string;
  progress: string;
}
interface ElementLoadInfo {
  status: "initial" | "loading" | "loaded";
  classes: ElementClasses;
}
const elementsClasses: Map<Element, ElementLoadInfo> = new Map();

const CSS_ANIMATION_TIMEOUT = 500;

function loadImage(el: Element) {
  const data = elementsClasses.get(el);
  const imageElement = el.querySelector("img");
  if (!data || !imageElement) {
    return;
  }
  let shouldContinue = false;

  const hasLazySrc = !!imageElement.dataset.src;
  const isSrcChanged = imageElement.src !== (imageElement.dataset?.src ?? "");

  const hasLazySrcSet = !!imageElement.dataset.srcset;
  const isSrcSetChanged = imageElement.srcset !== (imageElement.dataset?.srcset ?? "");

  if (hasLazySrc && isSrcChanged) shouldContinue = true;
  if (hasLazySrcSet && isSrcSetChanged) shouldContinue = true;
  if (!shouldContinue) {
    return;
  }

  elementsClasses.set(el, { ...data, status: "loading" });
  const timeout = setTimeout(() => el.classList.add(data.classes.progress), CSS_ANIMATION_TIMEOUT);

  imageElement.addEventListener(
    "load",
    () => {
      elementsClasses.set(el, { ...data, status: "loaded" });
      setTimeout(() => el.classList.add(data.classes.loaded), 100);
      clearTimeout(timeout);
    },
    { once: true }
  );
  if (hasLazySrcSet) imageElement.srcset = imageElement.dataset?.srcset ?? imageElement.srcset;
  if (hasLazySrc) imageElement.src = imageElement.dataset?.src ?? imageElement.src;
}

function getObserver() {
  const options: IntersectionObserverInit = {
    root: null,
    threshold: 0,
    rootMargin: "100%",
  };
  if (!observer) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadImage(entry.target);
          observer && observer.unobserve(entry.target);
        }
      });
    }, options);
  }
  return observer;
}

function setObserver(el: HTMLElement, opt: ElementClasses) {
  const data = elementsClasses.get(el);
  if (!data) {
    elementsClasses.set(el, { status: "initial", classes: opt });
    el.classList.add(opt.initial);
    getObserver().observe(el);
  } else {
    loadImage(el);
  }
}

function removeObserver(el: Element) {
  elementsClasses.delete(el);
  getObserver().unobserve(el);
}

export default {
  install(Vue: VueConstructor) {
    Vue.directive("lazy-load", {
      inserted(el, binding) {
        setObserver(el, {
          initial: binding?.value?.initial ?? "lazy-loading",
          progress: binding?.value?.progress ?? "__lazy-progress",
          loaded: binding?.value?.loaded ?? "__lazy-done",
        });
      },
      componentUpdated(el, binding) {
        setObserver(el, {
          initial: binding?.value?.initial ?? "lazy-loading",
          progress: binding?.value?.progress ?? "__lazy-progress",
          loaded: binding?.value?.loaded ?? "__lazy-done",
        });
      },
      unbind(el) {
        removeObserver(el);
      },
    });
  },
};
